import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Cog,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstallationSystems = () => {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [systemToDelete, setSystemToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    service_interval_days: 90,
    service_interval_label: "3 months",
  });

  const fetchSystems = async () => {
    try {
      const response = await axios.get(`${API}/installation-systems`, {
        withCredentials: true,
      });
      setSystems(response.data);
    } catch (error) {
      console.error("Error fetching systems:", error);
      toast.error("Failed to load installation systems");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystems();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSystem) {
        await axios.put(
          `${API}/installation-systems/${editingSystem.system_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success("System updated successfully");
      } else {
        await axios.post(`${API}/installation-systems`, formData, {
          withCredentials: true,
        });
        toast.success("System created successfully");
      }
      setSheetOpen(false);
      resetForm();
      fetchSystems();
    } catch (error) {
      console.error("Error saving system:", error);
      toast.error("Failed to save system");
    }
  };

  const handleEdit = (system) => {
    setEditingSystem(system);
    setFormData({
      name: system.name,
      description: system.description || "",
      service_interval_days: system.service_interval_days,
      service_interval_label: system.service_interval_label,
    });
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    if (!systemToDelete) return;
    try {
      await axios.delete(
        `${API}/installation-systems/${systemToDelete.system_id}`,
        { withCredentials: true }
      );
      toast.success("System deleted successfully");
      setDeleteDialogOpen(false);
      setSystemToDelete(null);
      fetchSystems();
    } catch (error) {
      console.error("Error deleting system:", error);
      toast.error("Failed to delete system");
    }
  };

  const resetForm = () => {
    setEditingSystem(null);
    setFormData({
      name: "",
      description: "",
      service_interval_days: 90,
      service_interval_label: "3 months",
    });
  };

  const intervalPresets = [
    { days: 30, label: "1 month" },
    { days: 60, label: "2 months" },
    { days: 90, label: "3 months" },
    { days: 180, label: "6 months" },
    { days: 365, label: "1 year" },
  ];

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="installation-systems-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Installation Systems
          </h1>
          <p className="text-slate-500 mt-1">
            Manage installation types and their service intervals
          </p>
        </div>
        <Button
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          onClick={() => setSheetOpen(true)}
          data-testid="add-system-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add System
        </Button>
      </div>

      {/* Systems Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {systems.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>System Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Service Interval</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {systems.map((system) => (
                  <TableRow key={system.system_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleEdit(system)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-sm flex items-center justify-center">
                          <Cog className="h-5 w-5 text-indigo-600" />
                        </div>
                        <span className="font-medium">{system.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-md truncate">
                      {system.description || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span>{system.service_interval_label}</span>
                        <span className="text-slate-400">
                          ({system.service_interval_days} days)
                        </span>
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(system)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSystemToDelete(system);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Cog className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No installation systems
              </h3>
              <p className="text-slate-500 mb-6">
                Create your first installation system type
              </p>
              <Button
                onClick={() => setSheetOpen(true)}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add System
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editingSystem ? "Edit System" : "Add Installation System"}
            </SheetTitle>
            <SheetDescription>
              {editingSystem
                ? "Update the installation system details"
                : "Create a new type of installation system"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">System Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Draught Beer System"
                required
                className="rounded-sm"
                data-testid="system-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe this type of installation"
                rows={3}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Service Interval</Label>
              <div className="flex flex-wrap gap-2">
                {intervalPresets.map((preset) => (
                  <Button
                    key={preset.days}
                    type="button"
                    variant={
                      formData.service_interval_days === preset.days
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    className="rounded-sm"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        service_interval_days: preset.days,
                        service_interval_label: preset.label,
                      })
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="interval_days">Days</Label>
                <Input
                  id="interval_days"
                  type="number"
                  min="1"
                  value={formData.service_interval_days}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      service_interval_days: parseInt(e.target.value) || 90,
                    })
                  }
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interval_label">Label</Label>
                <Input
                  id="interval_label"
                  value={formData.service_interval_label}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      service_interval_label: e.target.value,
                    })
                  }
                  placeholder="e.g., 3 months"
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSheetOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="save-system-btn"
              >
                {editingSystem ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Installation System</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{systemToDelete?.name}"? This may
              affect existing customer installations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InstallationSystems;
